import * as React from 'react';
import { noop } from '@deltasierra/utilities-object';
import { useDebounceFn } from '@deltasierra/react-hooks';
import { Loading } from '../Generic';

type UseInfiniteScrollHandlerParams = {
    debounceTimeMs?: number;
    hasMore: boolean;
    heightTolerance?: number;
    loading?: boolean;
    onLoadMore?: () => void;
};

const useInfiniteScrollHandler = <T extends HTMLElement = HTMLElement>({
    debounceTimeMs = 300,
    hasMore,
    heightTolerance = 50,
    loading = false,
    onLoadMore = noop,
}: UseInfiniteScrollHandlerParams): typeof handleOnScroll => {
    const debouncedOnLoadMore = useDebounceFn(onLoadMore, debounceTimeMs, true);

    const handleOnScroll: React.UIEventHandler<T> = React.useCallback(
        event => {
            const { currentTarget } = event;
            const scrollPosition = currentTarget.scrollTop + currentTarget.clientHeight;
            const isNearBottom = scrollPosition + heightTolerance >= currentTarget.scrollHeight;
            if (!loading && isNearBottom && hasMore) {
                debouncedOnLoadMore();
            }
        },
        [debouncedOnLoadMore, hasMore, heightTolerance, loading],
    );

    return handleOnScroll;
};

export type InfiniteScrollProps = React.HTMLAttributes<HTMLDivElement> & {
    children?: React.ReactNode;
    debounceTimeMs?: number;
    hasMore?: boolean;
    heightTolerance?: number;
    loading?: boolean;
    loadingComponent?: React.ReactNode;
    onLoadMore?: () => void;
};

export const InfiniteScroll: React.FunctionComponent<InfiniteScrollProps> = ({
    children,
    debounceTimeMs = 300,
    hasMore = true,
    heightTolerance = 50,
    loading,
    loadingComponent,
    onLoadMore = noop,
    ...props
}) => {
    const handleOnScroll = useInfiniteScrollHandler<HTMLDivElement>({
        debounceTimeMs,
        hasMore,
        heightTolerance,
        loading,
        onLoadMore,
    });

    return (
        <div onScroll={handleOnScroll} {...props}>
            {children}
            {loadingComponent ? (
                <>{loadingComponent}</>
            ) : (
                <div
                    style={{
                        display: hasMore ? 'block' : 'none',
                        opacity: loading ? 1 : 0,
                        textAlign: !loadingComponent ? 'center' : undefined,
                    }}
                >
                    <Loading inline size="small" />
                </div>
            )}
        </div>
    );
};
InfiniteScroll.displayName = 'InfiniteScroll';
